import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  Home, 
  Plus, 
  ArrowLeft,
  CheckCircle,
  Clock,
  AlertCircle,
  Trash2,
  Edit,
  Zap,
  ChevronRight,
  LayoutDashboard,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  RefreshCw,
  ExternalLink,
  AlertTriangle,
  Shield,
  Handshake
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger } from "../components/ui/alert-dialog";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const propertyStatusConfig = {
  draft: { label: "Brouillon", color: "bg-gray-100 text-gray-800", icon: Edit },
  ready_to_submit: { label: "Prêt à activer", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  submitted: { label: "Activation en cours", color: "bg-blue-100 text-blue-800", icon: Clock },
  active: { label: "Actif", color: "bg-green-100 text-green-800", icon: CheckCircle },
  review_required: { label: "Action requise", color: "bg-orange-100 text-orange-800", icon: AlertTriangle },
  blocked: { label: "Bloqué", color: "bg-red-100 text-red-800", icon: AlertCircle } };

const contractStatusConfig = {
  draft: { label: "Brouillon", color: "text-gray-500" },
  api_pending: { label: "Vérification en cours", color: "text-blue-500" },
  active: { label: "Actif", color: "text-green-600" },
  suspended: { label: "Suspendu", color: "text-orange-500" },
  expired: { label: "Expiré", color: "text-gray-500" },
  cancelled: { label: "Annulé", color: "text-red-500" },
  review_required: { label: "Révision requise", color: "text-orange-500" },
  blocked: { label: "Bloqué", color: "text-red-500" } };

const PropertiesPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [properties, setProperties] = useState([]);
  const [orders, setOrders] = useState([]);
  const [progression, setProgression] = useState({});
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activatingId, setActivatingId] = useState(null);
  const [retryingId, setRetryingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/dashboard/`, {
        withCredentials: true });
      setProperties(data.properties || []);
      setOrders(data.orders || []);
      setProgression(data.progression || {});
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleDelete = async (propertyId) => {
    try {
      await axios.delete(`${API_URL}/api/properties/${propertyId}`, {
        withCredentials: true });
      toast.success("Logement supprimé");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const handleActivate = async (propertyId) => {
    setActivatingId(propertyId);
    try {
      const { data } = await axios.post(`${API_URL}/api/properties/${propertyId}/activate`, {}, {
        withCredentials: true });
      toast.success(data.message || "Protection activée avec succès !");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'activation");
      fetchData(); // Refresh to show updated status
    } finally {
      setActivatingId(null);
    }
  };

  const handleRetryActivation = async (propertyId) => {
    setRetryingId(propertyId);
    try {
      const { data } = await axios.post(`${API_URL}/api/properties/${propertyId}/retry-activation`, {}, {
        withCredentials: true });
      toast.success(data.message || "Protection activée avec succès !");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la réactivation");
      fetchData();
    } finally {
      setRetryingId(null);
    }
  };

  const paidOrders = orders.filter(o => [
    "paid_pending_property_data",
    "partially_completed",
    "ready_to_activate",
    "completed"
  ].includes(o.status));

  const canAddProperty = progression.remaining > 0;

  const progressPercent = progression.total_purchased > 0 
    ? Math.round((progression.completed_properties / progression.total_purchased) * 100)
    : 0;

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric" });
  };

  return (
    <div className="min-h-screen bg-slate-50 font-['DM_Sans']">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between h-16 px-6 border-b border-slate-100">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-hostprotect.png" alt="HostProtect" className="h-9 w-auto" />
            </Link>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-slate-400 hover:text-slate-600">
              <X className="w-6 h-6" />
            </button>
          </div>
          <nav className="flex-1 px-4 py-6 space-y-2">
            {user?.role === "admin" && (
              <Link to="/admin" className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" data-testid="nav-admin">
                <Shield className="w-5 h-5" />
                Administration
              </Link>
            )}
            <Link to="/dashboard" className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg">
              <LayoutDashboard className="w-5 h-5" />
              Tableau de bord
            </Link>
            <Link to="/logements" className="flex items-center gap-3 px-4 py-3 text-[#19294C] bg-slate-100 rounded-lg font-medium">
              <Home className="w-5 h-5" />
              Mes logements
            </Link>
            <button className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg w-full text-left">
              <FileText className="w-5 h-5" />
              Mes commandes
            </button>
            <button className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg w-full text-left">
              <Settings className="w-5 h-5" />
              Paramètres
            </button>
            <Link to="/partenaire" className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" data-testid="nav-affiliate">
              <Handshake className="w-5 h-5" /> Programme partenaire
            </Link>
          </nav>
          <div className="p-4 border-t border-slate-100">
            <div className="px-4 py-3 mb-2">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
            <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg w-full text-left">
              <LogOut className="w-5 h-5" />
              Déconnexion
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-slate-600 hover:text-slate-900">
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-semibold text-[#19294C] font-['Outfit']">
              Mes logements
            </h1>
            <div></div>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8" data-testid="properties-content">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00B8E4]"></div>
            </div>
          ) : (
            <>
              {/* Warning Banner */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <div className="flex gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    <strong>Important :</strong> La protection prendra effet uniquement après validation complète des informations du logement et activation du contrat.
                  </p>
                </div>
              </div>

              {/* Progression Card */}
              {progression.total_purchased > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6" data-testid="progression-card">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-[#19294C] font-['Outfit']">
                      Progression
                    </h3>
                    <span className="text-2xl font-bold text-[#00B8E4]">{progressPercent}%</span>
                  </div>
                  <Progress value={progressPercent} className="h-3 mb-4" />
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-[#19294C]">{progression.total_purchased}</p>
                      <p className="text-xs text-slate-500">Achetées</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-[#00B8E4]">{progression.completed_properties}</p>
                      <p className="text-xs text-slate-500">Actives</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-orange-500">{progression.remaining}</p>
                      <p className="text-xs text-slate-500">Restantes</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Add Property Button */}
              {canAddProperty && paidOrders.length > 0 && (
                <div className="mb-6">
                  <Link to={`/logements/nouveau/${paidOrders[0].id}`}>
                    <Button className="bg-[#00B8E4] hover:bg-[#00a5ce] text-white" data-testid="add-property-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter un logement
                    </Button>
                  </Link>
                </div>
              )}

              {/* No properties state */}
              {properties.length === 0 && progression.total_purchased === 0 && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center">
                  <Home className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucun logement</h3>
                  <p className="text-slate-500 mb-6">
                    Souscrivez à une protection pour commencer à ajouter vos logements.
                  </p>
                  <Link to="/checkout">
                    <Button className="bg-[#00B8E4] hover:bg-[#00a5ce] text-white">
                      Souscrire maintenant
                    </Button>
                  </Link>
                </div>
              )}

              {/* Properties awaiting creation */}
              {properties.length === 0 && progression.total_purchased > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center">
                  <Home className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Créez vos logements</h3>
                  <p className="text-slate-500 mb-6">
                    Vous avez {progression.total_purchased} protection{progression.total_purchased > 1 ? 's' : ''} en attente de configuration.
                  </p>
                  <Link to={`/logements/nouveau/${paidOrders[0]?.id}`}>
                    <Button className="bg-[#00B8E4] hover:bg-[#00a5ce] text-white" data-testid="create-first-property-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Créer mon premier logement
                    </Button>
                  </Link>
                </div>
              )}

              {/* Properties List */}
              {properties.length > 0 && (
                <div className="space-y-4">
                  {properties.map((property) => {
                    const status = propertyStatusConfig[property.status] || propertyStatusConfig.draft;
                    const StatusIcon = status.icon;
                    const canEdit = ["draft", "ready_to_submit", "review_required"].includes(property.status);
                    const canDelete = !["active", "submitted"].includes(property.status);
                    const canActivate = property.status === "ready_to_submit";
                    const canRetry = property.status === "review_required";
                    const contract = property.contract;

                    return (
                      <div
                        key={property.id}
                        className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
                        data-testid={`property-item-${property.id}`}
                      >
                        <div className="px-6 py-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${status.color.split(' ')[0]}`}>
                                <StatusIcon className={`w-6 h-6 ${status.color.split(' ')[1]}`} />
                              </div>
                              <div>
                                <p className="font-semibold text-slate-900">{property.name}</p>
                                <p className="text-sm text-slate-500">
                                  {property.risk_address?.street}, {property.risk_address?.postal_code} {property.risk_address?.city}
                                </p>
                                <p className="text-xs text-slate-400 mt-1">
                                  {property.surface} m² • {property.exploitation_type?.replace(/_/g, ' ')}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${status.color}`}>
                                {status.label}
                              </span>
                              
                              {canActivate && (
                                <Button
                                  onClick={() => handleActivate(property.id)}
                                  disabled={activatingId === property.id}
                                  className="bg-[#00B8E4] hover:bg-[#00a5ce] text-white"
                                  data-testid={`activate-btn-${property.id}`}
                                >
                                  {activatingId === property.id ? (
                                    <>
                                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                                      Activation en cours...
                                    </>
                                  ) : (
                                    <>
                                      <Zap className="w-4 h-4 mr-2" />
                                      Activer ma protection
                                    </>
                                  )}
                                </Button>
                              )}

                              {canRetry && (
                                <Button
                                  onClick={() => handleRetryActivation(property.id)}
                                  disabled={retryingId === property.id}
                                  className="bg-orange-500 hover:bg-orange-600 text-white"
                                  data-testid={`retry-btn-${property.id}`}
                                >
                                  {retryingId === property.id ? (
                                    <>
                                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                                      Réessai...
                                    </>
                                  ) : (
                                    <>
                                      <RefreshCw className="w-4 h-4 mr-2" />
                                      Réessayer l'activation
                                    </>
                                  )}
                                </Button>
                              )}

                              {canEdit && (
                                <Link to={`/logements/${property.id}/editer`}>
                                  <Button variant="outline" size="icon" data-testid={`edit-btn-${property.id}`}>
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                </Link>
                              )}

                              {canDelete && (
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="outline" size="icon" className="text-red-600 hover:bg-red-50" data-testid={`delete-btn-${property.id}`}>
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Supprimer ce logement ?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Cette action est irréversible. Le logement "{property.name}" sera définitivement supprimé.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Annuler</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => handleDelete(property.id)}
                                        className="bg-red-600 hover:bg-red-700"
                                      >
                                        Supprimer
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Contract Info for Active Properties */}
                        {contract && contract.status === "active" && (
                          <div className="px-6 py-4 bg-green-50 border-t border-green-100">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                                <div>
                                  <p className="text-sm font-medium text-green-800">
                                    Protection active
                                  </p>
                                  <p className="text-xs text-green-600">
                                    Contrat n°{contract.meetch_reference || contract.id.slice(0, 8)} • Activé le {formatDate(contract.activated_at)}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                {contract.certificate_url && (
                                  <a
                                    href={contract.certificate_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-sm text-green-700 hover:text-green-800"
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                    Attestation
                                  </a>
                                )}
                                <Link
                                  to={`/logements/${property.id}/sinistres`}
                                  className="flex items-center gap-2 text-sm text-[#19294C] hover:text-[#1e3a8acc] font-medium"
                                  data-testid={`claims-link-${property.id}`}
                                >
                                  <FileText className="w-4 h-4" />
                                  Sinistres
                                </Link>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Suspended Contract Info */}
                        {contract && contract.status === "suspended" && (
                          <div className="px-6 py-4 bg-orange-50 border-t border-orange-200" data-testid={`contract-suspended-${property.id}`}>
                            <div className="flex items-center gap-3">
                              <AlertCircle className="w-5 h-5 text-orange-600" />
                              <div>
                                <p className="text-sm font-medium text-orange-800">Protection suspendue</p>
                                <p className="text-xs text-orange-600">
                                  Contactez le support pour plus d'informations — support@hostprotect.io
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* API Pending Contract Info */}
                        {contract && contract.status === "api_pending" && (
                          <div className="px-6 py-4 bg-blue-50 border-t border-blue-100" data-testid={`contract-pending-${property.id}`}>
                            <div className="flex items-center gap-3">
                              <Clock className="w-5 h-5 text-blue-600" />
                              <p className="text-sm text-blue-800">
                                Activation en cours de vérification. Vous recevrez une confirmation par email.
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Review Required Info */}
                        {property.status === "review_required" && (
                          <div className="px-6 py-4 bg-orange-50 border-t border-orange-100">
                            <div className="flex items-center gap-3">
                              <AlertTriangle className="w-5 h-5 text-orange-600" />
                              <p className="text-sm text-orange-800">
                                L'activation n'a pas pu être finalisée. Veuillez réessayer ou contacter le support.
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default PropertiesPage;
